import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TVCard from '@/components/base/TVCard';
import { tvSeriesData, categories } from '@/mocks/tvSeries';

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const hotSeries = tvSeriesData.filter((s) => s.isHot).slice(0, 5);
  const newSeries = tvSeriesData.filter((s) => s.isNew);
  const topRated = [...tvSeriesData].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const categoriesToShow = categories.filter((c) => c.slug !== 'all');

  useEffect(() => {
    if (hotSeries.length === 0) return;
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % hotSeries.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [hotSeries.length]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative w-full h-[500px] md:h-[650px] flex items-center overflow-hidden"
        aria-label="热门推荐"
      >
        <div className="absolute inset-0">
          {hotSeries.map((series, idx) => (
            <div
              key={series.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                idx === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={series.image}
                alt={series.title}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
        </div>

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
              窝窝影院 - 免费追剧，精彩不断
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-6 leading-relaxed">
              电视剧全集免费在线观看，最新热播剧集大全，高清不缓冲流畅播放，无广告全免费，每日更新热门电视剧。
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/article/${hotSeries[heroIndex]?.id}`}
                className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                立即观看
              </Link>
              <Link
                to="/category/all"
                className="px-6 py-2.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/30 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览全部剧集
              </Link>
            </div>

            <div className="flex items-center gap-2 mt-8">
              {hotSeries.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setHeroIndex(idx)}
                  className={`w-8 h-1 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === heroIndex ? 'bg-primary-500 w-12' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`切换到第${idx + 1}张`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 -mt-8 relative z-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {categoriesToShow.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 hover:bg-primary-50 transition-all duration-200 group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-200">
                <i className={`${cat.icon} text-primary-500 group-hover:text-white text-xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Hot & New Section */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-20">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              热播推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">全网热播电视剧精选，追剧党必看</p>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 flex items-center gap-1 whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {[...hotSeries, ...newSeries].slice(0, 10).map((series) => (
            <TVCard key={series.id} series={series} />
          ))}
        </div>
      </section>

      {/* Top Rated Section */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-20">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              高分推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">口碑爆棚，评分最高的精品剧集</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
          {topRated.map((series) => (
            <TVCard key={series.id} series={series} />
          ))}
        </div>
      </section>

      {/* By Category */}
      {categoriesToShow.map((cat) => {
        const catSeries = tvSeriesData.filter((s) => s.categorySlug === cat.slug).slice(0, 5);
        if (catSeries.length === 0) return null;
        return (
          <section
            key={cat.slug}
            className="w-full max-w-[1280px] mx-auto px-4 md:px-6 mt-14 md:mt-20"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${cat.icon} text-primary-500 text-lg w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                    {cat.name}
                  </h2>
                  <p className="text-sm text-foreground-500 mt-0.5">{cat.description}</p>
                </div>
              </div>
              <Link
                to={`/category/${cat.slug}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 flex items-center gap-1 whitespace-nowrap"
              >
                更多{cat.name}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {catSeries.map((series) => (
                <TVCard key={series.id} series={series} />
              ))}
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="w-full mt-20 py-16 md:py-20 bg-background-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-primary-500 blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-accent-500 blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
            追剧从未如此简单
          </h2>
          <p className="text-base text-foreground-400 max-w-xl mx-auto mb-8">
            高清不缓冲，无广告免费观看。每日更新热门电视剧，随时随地，畅享追剧乐趣。
          </p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            开始追剧
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 mt-20 mb-20">
        <div className="text-center mb-10">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 mt-2">关于窝窝影院，你可能想了解的问题</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {[
            { q: '窝窝影院的电视剧都是免费的吗？', a: '是的，窝窝影院所有电视剧全部免费在线观看，无需付费，无需注册，打开即看。' },
            { q: '观看需要安装插件吗？', a: '不需要安装任何插件。窝窝影院采用先进的HTML5播放技术，浏览器原生支持，流畅播放。' },
            { q: '支持哪些设备观看？', a: '支持电脑、平板、手机等所有主流设备。无论你在哪里，只要有网络就能看。' },
            { q: '电视剧更新频率怎么样？', a: '我们每日更新最新热播剧集，确保你能第一时间观看到最新内容。热门剧集与电视台同步更新。' },
          ].map((faq, idx) => (
            <div key={idx} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />

      {/* SEO Schema Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '窝窝影院',
            url: 'https://wowocinema.com/',
            description: '窝窝影院为您提供电视剧全集免费在线观看，最新热播剧集大全，高清不缓冲流畅播放，无广告全免费，每日更新热门电视剧，追剧首选。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://wowocinema.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '窝窝影院',
            url: 'https://wowocinema.com/',
            description: '窝窝影院 - 免费电视剧在线观看平台',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '窝窝影院的电视剧都是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，窝窝影院所有电视剧全部免费在线观看，无需付费，无需注册，打开即看。' } },
              { '@type': 'Question', name: '观看需要安装插件吗？', acceptedAnswer: { '@type': 'Answer', text: '不需要安装任何插件。窝窝影院采用先进的HTML5播放技术，浏览器原生支持，流畅播放。' } },
              { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '支持电脑、平板、手机等所有主流设备。无论你在哪里，只要有网络就能看。' } },
              { '@type': 'Question', name: '电视剧更新频率怎么样？', acceptedAnswer: { '@type': 'Answer', text: '我们每日更新最新热播剧集，确保你能第一时间观看到最新内容。热门剧集与电视台同步更新。' } },
            ],
          }),
        }}
      />
    </div>
  );
}